import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import OrganizationsList from "./OrganizationsList";
import {
  getCountries,
  getOrganizations,
  getParentOrganizationName,
} from "../../api/organizationsApi";
import { OrganizationResponseDto } from "../common/ObjectTypes";
import { OrganizationSearchQueryType } from "../common/QueryTypes";
import { useProfile } from "../common/ProfileProvider";
import { pageSize } from "../../api/apiUtils";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";

const ManageOrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<OrganizationResponseDto[]>(
    [],
  );
  const [parentOrgs, setParentOrgs] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [countries, setCountries] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [resultsLoaded, setResultsLoaded] = useState(false);
  const { profile, profileLoading } = useProfile();
  const [hasOrganizations, setHasOrganizations] = useState(false);
  const { t } = useTranslation();

  const initialOrganizationSearchQuery = {
    skip: localStorage.getItem("orgTableSkip")
      ? parseInt(localStorage.getItem("orgTableSkip") as string)
      : 0,
    take: pageSize,
    sort_by: "org_name",
    sort_direction: "asc",
    primary_contact_email: "",
    country_id: undefined,
  };

  const [organizationsSearchQuery, setOrganizationsSearchQuery] =
    useState<OrganizationSearchQueryType>(initialOrganizationSearchQuery);
  const [tempOrganizationsSearchQuery, setTempOrganizationsSearchQuery] =
    useState<OrganizationSearchQueryType>(initialOrganizationSearchQuery);

  /*
   * Fetch organizations when search query changes or profile is loaded.
   */
  const processOrganizations = async (orgList: OrganizationResponseDto[]) => {
    const orgsWithParentOrgs: Array<{
      parent_org_name: string | undefined;
      parent_org_id: number | undefined;
    }> = [];

    await Promise.all(
      orgList.map(async (org) => {
        if (org.parent_organization_id) {
          const parentOrgName: string = await getParentOrganizationName(
            org.org_id as number,
          );
          let result = {
            parent_org_name: parentOrgName,
            parent_org_id: org.parent_organization_id,
          };
          orgsWithParentOrgs.push(result);
        }
      }),
    );

    setParentOrgs(orgsWithParentOrgs);
  };

  useEffect(() => {
    const loadOrganizations = async (query: any) => {
      try {
        const temp = await getOrganizations(query);
        setOrganizations(temp.organizations);
        await processOrganizations(temp.organizations);
        setTotalCount(temp.count);
        setResultsLoaded(true);
      } catch (err) {
        console.error("Error loading organizations:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchOrganizations = async () => {
      setResultsLoaded(false);
      await loadOrganizations(organizationsSearchQuery);
    };
    if (!profileLoading) {
      fetchOrganizations();
    }
  }, [organizationsSearchQuery, profileLoading]);

  /*
   * Fetch countries when language changes.
   */
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const temp = await getCountries(i18n.language);
        setCountries(temp);
      } catch (error) {
        console.error(t("Error loading countries:"), error);
      }
    };

    loadCountries();
  }, [t]);

  /*
   * Fetch initial data once profile is loaded.
   */
  useEffect(() => {
    if (!profileLoading && profile) {
      const temp = profile?.org_roles.length > 0 || profile?.is_admin;
      setHasOrganizations(temp);
    }
  }, [profileLoading, profile]);

  /**
   * Fetch orgs when search query changes for country ID.
   */
  useEffect(() => {
    handleSearch();
  }, [tempOrganizationsSearchQuery.country_id]);

  const handleChangeSearchQuery = (e: any) => {
    if (e) {
      let name: string;
      let value: string | boolean;
      name = e.target.name;
      value = e.target.value;
      setTempOrganizationsSearchQuery((prevQuery) => ({
        ...prevQuery,
        [name]: value,
        skip: 0,
      }));
    } else {
      setTempOrganizationsSearchQuery((prevQuery) => ({
        ...prevQuery,
        country_id: undefined,
        skip: 0,
      }));
    }
  };

  const handleSearch = async () => {
    setOrganizationsSearchQuery(tempOrganizationsSearchQuery);
  };

  const getParentOrgName = (orgId: number): string => {
    const parentOrg = parentOrgs.find((org) => org.parent_org_id === orgId);
    return parentOrg ? parentOrg.parent_org_name : ""; // Return organization name or an empty string if not found
  };

  const handleAddOrg = () => {
    navigate("/addOrganization");
  };
  const handleSelectOrg = (guid: string) => {
    navigate("/organizations/" + guid + "/overview");
  };

  const handlePageClick = (e: any) => {
    setOrganizationsSearchQuery((prevQuery) => ({
      ...prevQuery,
      skip: e.selected * pageSize,
    }));
    localStorage.setItem("orgTableSkip", (e.selected * pageSize).toString());
    setResultsLoaded(false);
  };

  return (
    <OrganizationsList
      organizations={organizations}
      totalCount={totalCount}
      take={pageSize}
      offset={organizationsSearchQuery.skip}
      onPageClicked={handlePageClick}
      countries={countries}
      onAddOrg={handleAddOrg}
      onSelectOrg={handleSelectOrg}
      getParentOrgName={getParentOrgName}
      loading={loading}
      onChangeSearchQuery={handleChangeSearchQuery}
      onSearch={handleSearch}
      tempOrganizationsSearchQuery={tempOrganizationsSearchQuery}
      resultsLoaded={resultsLoaded}
      hasOrganizations={hasOrganizations}
    />
  );
};

export default ManageOrganizationsPage;
